const data = {
  slug: "reposition-or-transform",
  number: "03",
  title: "Reposition & Transform",
  intro: [
    "The business has moved forward. What surrounds it has not. Markets, customers, categories and ambitions change, but brands, propositions, business models and organisations often continue carrying the assumptions of an earlier stage.",
    "When the current version of the business can no longer support where it needs to go, the answer is not a cosmetic refresh. Sometimes the business must decide what to preserve, what to leave behind and what must fundamentally change.",
  ],
  sections: [
    {
      kicker: "What it looks like",
      heading: "Increasingly out of step with the market.",
      body: [
        "Transformation rarely begins with one dramatic failure. More often, the business begins to feel out of step with its market, customers or own ambition. You may be seeing:",
      ],
      items: [
        "A brand that no longer feels relevant or distinctive",
        "Customers struggling to understand why the business is different",
        "A proposition built for an earlier stage of the company",
        "Growth slowing despite a strong category opportunity",
        "New competitors defining the market more clearly",
        "Multiple businesses, brands or offers creating confusion",
        "A business model that limits future growth",
        "Transformation initiatives moving without one unifying direction",
        "Teams interpreting the strategy differently",
        "Technology or processes holding back the customer experience",
        "Leadership agreeing change is required but not agreeing on what must change",
        "The organisation still operating according to old assumptions",
      ],
    },
    {
      kicker: "The real problem",
      heading: "A new line cannot solve an old business problem.",
      body: [
        "Changing the language without changing the underlying value will not reposition the business. A new identity cannot compensate for an unclear proposition. A campaign cannot create relevance the product or experience does not deliver.",
        "Repositioning works when it clarifies what the business will stand for, who it will matter to and how that promise will be delivered. Transformation works when the strategy, operating model and organisation change with it.",
      ],
    },
    {
      kicker: "Possible causes",
      heading: "The visible expression may be only one part of the problem.",
      items: [
        "Strategic direction, no clear choice about the future centre of gravity.",
        "Market position, the space once occupied is now crowded or less relevant.",
        "Customer proposition, unclear why customers should choose, stay or pay more.",
        "Brand architecture, multiple brands or sub-brands competing and weakening one another.",
        "Business model, revenue model, channels or cost structure limiting growth.",
        "Customer experience, the promise is strong in communication but inconsistent in delivery.",
        "Technology and process, legacy systems prevent the required experience or efficiency.",
        "Organisation and capability, the future strategy needs different roles, skills and leadership.",
        "Culture and behaviour, people asked to act differently while the old model is still rewarded.",
        "Execution alignment, isolated pieces of transformation without one connected roadmap.",
      ],
    },
    {
      kicker: "Common mistakes",
      heading: "Change fails when transformation is treated as a set of outputs.",
      items: [
        "Begin with a new identity",
        "Define the position only through communication",
        "Copy the category leader",
        "Attempt to appeal to everyone",
        "Change everything at once",
        "Preserve every legacy offering",
        "Announce transformation before building readiness",
        "Separate strategy from culture",
        "Treat implementation as a downstream task",
      ],
    },
    {
      kicker: "Our approach",
      heading: "How SWITCH diagnoses the need to change",
      body: [
        "Before deciding how the business should change, we determine why the current model is no longer enough. The diagnostic is shaped around the mandate and may examine:",
      ],
      items: [
        "Business ambition",
        "Market and category",
        "Customer proposition",
        "Brand and portfolio",
        "Business model",
        "Experience and delivery",
        "Technology and process",
        "Organisation and culture",
        "Execution readiness",
      ],
    },
    {
      kicker: "What to preserve",
      heading: "Transformation is not the rejection of everything before.",
      body: [
        "The strongest businesses often possess valuable assets that should not be discarded, customer trust, founder conviction, product credibility, distribution strength, distinctive memory structures and a meaningful role in people's lives.",
        "The task is to identify which assets can become foundations for the future, and which legacy assumptions are now holding the business back. SWITCH separates what is valuable from what is merely familiar.",
      ],
    },
    {
      kicker: "Potential interventions",
      heading: "What the future business must become",
      body: ["The intervention depends on what the future business must become."],
      items: [
        "Reframing the growth strategy",
        "Rebuilding the proposition",
        "Repositioning the brand",
        "Redesigning the brand architecture",
        "Reshaping the portfolio",
        "Redesigning the business model",
        "Transforming the customer experience",
        "Modernising technology and process",
        "Redesigning the organisation",
        "Building the transformation operating system",
      ],
    },
    {
      kicker: "From diagnosis to execution",
      heading: "A position becomes real only when the business behaves accordingly.",
      body: [
        "If the business claims to be simpler, the experience must become simpler. If it claims to be premium, the product, pricing and service must support that promise. SWITCH connects the future position to product, experience, brand, sales, technology, organisation and commercial outcomes, so the position becomes a decision-making system, not a communications output.",
        "SWITCH works alongside leadership, internal teams and existing partners to move transformation into action, and remains involved until the agreed priorities are implemented.",
      ],
    },
  ],
};

export default data;
